import React, { useState } from 'react';
import { Outlet, useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Tooltip,
  Container,
  useTheme as useMuiTheme
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Chat as ChatIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Analytics as AnalyticsIcon,
  PersonAdd as PersonAddIcon,
  SmartToy as SmartToyIcon,
  Article as ArticleIcon,
  Payment as PaymentIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

export default function Layout() {
  const { user, logout } = useAuth();
  const muiTheme = useMuiTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Users', icon: <PeopleIcon />, path: '/users' },
    { text: 'Dr. Bill AI', icon: <ChatIcon />, path: '/drbill' },
    { text: 'Chat Analytics', icon: <AnalyticsIcon />, path: '/chat-analytics' },
  ];

  const getIconColor = (path, isExact = true) => {
    const isActive = isExact
      ? location.pathname === path
      : location.pathname.startsWith(path);
    return isActive ? 'secondary.main' : 'text.secondary';
  };

  const drawerContent = (
    <Box sx={{ width: drawerWidth }}>
      <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/" selected={location.pathname === '/'}>
            <ListItemIcon sx={{ color: getIconColor('/', true) }}>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/users" selected={location.pathname === '/users'}>
            <ListItemIcon sx={{ color: getIconColor('/users', true) }}>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Users" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/drbill" selected={location.pathname === '/drbill'}>
            <ListItemIcon sx={{ color: getIconColor('/drbill', true) }}>
              <SmartToyIcon />
            </ListItemIcon>
            <ListItemText primary="Ask Dr. Bill" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/chat-analytics" selected={location.pathname.startsWith('/chat-analytics') || location.pathname.startsWith('/chats/')}>
            <ListItemIcon sx={{ color: location.pathname.startsWith('/chat-analytics') || location.pathname.startsWith('/chats/') ? 'secondary.main' : 'text.secondary' }}>
              <AnalyticsIcon />
            </ListItemIcon>
            <ListItemText primary="Chat Analytics" />
          </ListItemButton>
        </ListItem>
        <Divider sx={{ my: 1 }} />
        <ListItem disablePadding>
          <ListItemButton component="a" href="https://drbillai.substack.com/" target="_blank" rel="noopener noreferrer">
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <ArticleIcon />
            </ListItemIcon>
            <ListItemText primary="Dr Bill Substack" />
            <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component="a" href="https://dashboard.stripe.com/dashboard" target="_blank" rel="noopener noreferrer">
            <ListItemIcon sx={{ color: 'text.secondary' }}>
              <PaymentIcon />
            </ListItemIcon>
            <ListItemText primary="Stripe Dashboard" />
            <OpenInNewIcon fontSize="small" sx={{ color: 'text.secondary', ml: 1 }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box
            component="img"
            src="/logo_light.png"
            alt="EpigenEx Logo"
            sx={{ height: 36, mr: 2, display: { xs: 'none', sm: 'block' } }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {user && (
            <>
              <Tooltip title="Account Settings">
                <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36 }}>
                    {user.username ? user.username[0].toUpperCase() : (user.email ? user.email[0].toUpperCase() : '?')}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                sx={{ mt: 1 }}
              >
                <MenuItem disabled sx={{ fontWeight: 500, py: 1, opacity: '1 !important', cursor: 'default' }}>
                  {user.username || user.email}
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          <Toolbar />
          {drawerContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          <Toolbar />
          {drawerContent}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: muiTheme.spacing(3),
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 10dvh)',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
}